<template>
  <div>
    <div class="pa-0 ma-0">
      <div class="body-overall">
        <v-row style="padding-top:64px!important"></v-row>
        <div :class="getGextPanel" class="pt-6" style="margin:0px!important;">
          <div class="fonts-white container pl-0 pr-1">
            <div class="overlay-grey-web">
              <!-- Header Section -->
              <div class="collections-header">
                <div class="header-content">
                  <v-icon class="header-icon">mdi-bookshelf</v-icon>
                  <h1 class="header-title">My Collections</h1>
                </div>
                <p class="header-subtitle">
                  Organize and manage your music collections and playlists
                </p>

                <!-- Action Buttons -->
                <div class="header-actions">
                  <v-btn
                    variant="outlined"
                    prepend-icon="mdi-refresh"
                    @click="refreshCollections()"
                    class="action-btn"
                  >
                    Refresh
                  </v-btn>
                  <v-btn
                    variant="outlined"
                    prepend-icon="mdi-plus"
                    @click="createCollection()"
                    class="action-btn create-btn"
                  >
                    Create Collection
                  </v-btn>
                </div>
              </div>

              <v-divider class="my-4" style="opacity:0.3;"></v-divider>

              <!-- Music Collections Section -->
              <div class="collections-section">
                <h2 class="section-title">
                  <v-icon left>mdi-playlist-music</v-icon>
                  Music Collections
                </h2>

                <div class="filters-row">
                  <v-text-field
                    v-model="searchQuery"
                    placeholder="Search collections..."
                    prepend-inner-icon="mdi-magnify"
                    density="comfortable"
                    hide-details
                    clearable
                    class="search-field"
                    variant="outlined"
                  ></v-text-field>

                  <v-select
                    v-model="sortBy"
                    :items="sortOptions"
                    label="Sort by"
                    density="comfortable"
                    hide-details
                    class="sort-select"
                    variant="outlined"
                  >
                    <template v-slot:prepend-inner>
                      <v-icon size="20">mdi-sort</v-icon>
                    </template>
                  </v-select>
                </div>
              </div>

              <v-divider class="my-4" style="opacity:0.3;"></v-divider>

              <!-- Collections Grid or Empty State -->
              <div class="collections-content">
                <!-- Collections Grid (when data available) -->
                <div v-if="filteredCollections.length > 0" class="collections-grid">
                  <div 
                    v-for="collection in filteredCollections" 
                    :key="collection.id"
                    class="collection-card"
                    @click="openCollection(collection.id)"
                  >
                    <div class="collection-cover">
                      <img 
                        v-if="collection.cover" 
                        :src="collection.cover" 
                        :alt="collection.name"
                      />
                      <div v-else class="collection-cover-placeholder">
                        <v-icon size="64" color="rgba(255,255,255,0.3)">mdi-music-box-multiple</v-icon>
                      </div>
                      
                      <div class="collection-overlay">
                        <v-btn
                          icon
                          size="large"
                          variant="text"
                          class="play-btn"
                          @click.stop="playCollection(collection.id)"
                        >
                          <v-icon size="48">mdi-play-circle</v-icon>
                        </v-btn>
                      </div>

                      <div class="collection-stats-overlay">
                        <v-chip size="x-small" class="tracks-chip">
                          <v-icon left size="12">mdi-music-note</v-icon>
                          {{ collection.trackCount || 0 }}
                        </v-chip>
                      </div>
                    </div>

                    <div class="collection-content">
                      <h3 class="collection-name">{{ collection.name }}</h3>
                      <p class="collection-meta">
                        <span v-if="collection.description" class="collection-description">
                          {{ collection.description }}
                        </span>
                        <span v-else class="collection-description empty">
                          No description
                        </span>
                      </p>
                      <div class="collection-info">
                        <div class="info-item">
                          <v-icon size="14">mdi-calendar</v-icon>
                          <span>{{ formatDate(collection.createdAt) }}</span>
                        </div>
                        <div class="info-item" v-if="collection.isPublic">
                          <v-icon size="14">mdi-earth</v-icon>
                          <span>Public</span>
                        </div>
                        <div class="info-item" v-else>
                          <v-icon size="14">mdi-lock</v-icon>
                          <span>Private</span>
                        </div>
                      </div>
                    </div>

                    <div class="collection-actions">
                      <v-btn
                        icon
                        size="small"
                        variant="text"
                        @click.stop="editCollection(collection.id)"
                        title="Edit"
                      >
                        <v-icon size="18">mdi-pencil</v-icon>
                      </v-btn>
                      <v-btn
                        icon
                        size="small"
                        variant="text"
                        @click.stop="shareCollection(collection.id)"
                        title="Share"
                      >
                        <v-icon size="18">mdi-share-variant</v-icon>
                      </v-btn>
                      <v-btn
                        icon
                        size="small"
                        variant="text"
                        color="error"
                        @click.stop="deleteCollection(collection.id)"
                        title="Delete"
                      >
                        <v-icon size="18">mdi-delete</v-icon>
                      </v-btn>
                    </div>
                  </div>
                </div>

                <!-- Empty State -->
                <div v-else class="collections-empty-state">
                  <div class="empty-icon-wrapper">
                    <v-icon size="80" color="grey">mdi-playlist-music-outline</v-icon>
                  </div>
                  <h3 class="empty-title">No Collections Yet</h3>
                  <p class="empty-subtitle">
                    Create your first collection to organize your favorite music
                  </p>
                  <div class="empty-actions">
                    <v-btn
                      variant="outlined"
                      prepend-icon="mdi-plus"
                      @click="createCollection()"
                      class="empty-action-btn"
                      size="large"
                    >
                      Create Your First Collection
                    </v-btn>
                  </div>
                  
                  <!-- Features Info -->
                  <div class="empty-features">
                    <div class="feature-item">
                      <v-icon size="20" color="success">mdi-check-circle</v-icon>
                      <span>Organize your music</span>
                    </div>
                    <div class="feature-item">
                      <v-icon size="20" color="success">mdi-check-circle</v-icon>
                      <span>Create playlists</span>
                    </div>
                    <div class="feature-item">
                      <v-icon size="20" color="success">mdi-check-circle</v-icon>
                      <span>Share with friends</span>
                    </div>
                    <div class="feature-item">
                      <v-icon size="20" color="success">mdi-check-circle</v-icon>
                      <span>Track your favorites</span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Tips Section -->
              <v-divider class="my-4" style="opacity:0.3;"></v-divider>

              <div class="tips-section">
                <h2 class="section-title">
                  <v-icon left>mdi-lightbulb</v-icon>
                  Tips & Tricks
                </h2>

                <div class="tips-grid">
                  <div class="tip-card">
                    <v-icon class="tip-icon" color="primary">mdi-playlist-plus</v-icon>
                    <h4 class="tip-title">Smart Playlists</h4>
                    <p class="tip-text">
                      Create dynamic playlists that automatically update based on your listening habits and preferences.
                    </p>
                  </div>

                  <div class="tip-card">
                    <v-icon class="tip-icon" color="success">mdi-share-variant</v-icon>
                    <h4 class="tip-title">Share Collections</h4>
                    <p class="tip-text">
                      Share your favorite collections with friends or make them public for everyone to discover.
                    </p>
                  </div>

                  <div class="tip-card">
                    <v-icon class="tip-icon" color="orange">mdi-folder-music</v-icon>
                    <h4 class="tip-title">Organize by Genre</h4>
                    <p class="tip-text">
                      Group your music by genre, mood, or activity to find the perfect soundtrack for any moment.
                    </p>
                  </div>

                    <div class="tip-card">
                      <v-icon class="tip-icon" color="purple">mdi-podcast</v-icon>
                      <h4 class="tip-title">Podcasts</h4>
                      <p class="tip-text">
                      Add and organize your favorite podcasts together with your music collections.
                      </p>
                    </div>
                </div>
              </div>

            </div>
          </div>
        </div>
        <v-row
          :style="(this.isMobileXs || this.isMobilePlayer3) ? 'padding-top:170px!important' : 'padding-top:123px!important'"
        ></v-row>
      </div>
    </div>
  </div>
</template>

<script>
import { useMobile } from '../composables/useMobile.js'
import { useMobilePlayer3 } from '../composables/useMobilePlayer3.js'
import { useMobileXs } from '../composables/useMobileXs.js'
import { toast } from "vue3-toastify";
import "vue3-toastify/dist/index.css";


export default {
  setup() {
    const isMobile = useMobile()
    const isMobileXs = useMobileXs()
    const isMobilePlayer3 = useMobilePlayer3();

    const notify = (message) => {
      toast(message, {
        theme: "dark",
        type: "success",
        position: "bottom-center",
        closeOnClick: false,
        pauseOnHover: false,
        autoClose: 1500,
        transition: "flip",
        dangerouslyHTMLString: true,
      });
    };

    const notifyError = (message) => {
      toast(message, {
        theme: "dark",
        type: "error",
        position: "bottom-center",
        closeOnClick: false,
        pauseOnHover: false,
        autoClose: 3000,
        transition: "flip",
        dangerouslyHTMLString: true,
      });
    };

    return { isMobile, isMobileXs, notify, notifyError, isMobilePlayer3 }
  },
  name: 'Collections',

  components: {

  },
  data: () => ({
    navbar: false,
    user: null,
    collections: [],
    searchQuery: "",
    sortBy: "recent",
    sortOptions: [
      { title: 'Most Recent', value: 'recent' },
      { title: 'Name A-Z', value: 'name_asc' },
      { title: 'Name Z-A', value: 'name_desc' },
      { title: 'Most Tracks', value: 'tracks' },
    ],

  }),
  mounted() {
    window.addEventListener('resize', this.checkScreenSize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkScreenSize);
  },
  beforeUnmount() {

  },
  destroyed() {
  },
  watch: {
    '$store.state.user': function () {
      this.user = this.$store.getters.getUserData;
    },
  },
  computed: {
    isfullScreen() {
      if (this.isMobile && this.isMobileXs) {
        return false;
      } else {
        return true;
      }
    },
    isLogged() {
      return this.$store.getters.getUserData != null
    },
    isDesktop() {
      if (!this.navbar) {
        return true;
      } else {
        return false;
      }
    },
    getGextPanel() {
      if (this.navbar) {
        if (this.isMobileXs) {
          return 'textPanelMobileXs'
        } else {
          return 'textPanelMobile';
        }
      } else return 'textPanel';
    },
    filteredCollections() {
      let filtered = [...this.collections];

      // Search filter
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        filtered = filtered.filter(collection => 
          collection.name.toLowerCase().includes(query) ||
          (collection.description && collection.description.toLowerCase().includes(query))
        );
      }

      // Sort
      switch(this.sortBy) {
        case 'name_asc':
          filtered.sort((a, b) => a.name.localeCompare(b.name));
          break;
        case 'name_desc':
          filtered.sort((a, b) => b.name.localeCompare(a.name));
          break;
        case 'tracks':
          filtered.sort((a, b) => (b.trackCount || 0) - (a.trackCount || 0));
          break;
        case 'recent':
        default:
          filtered.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      }

      return filtered;
    }
  },
  created() {
    if (this.$store.getters.getUserData != null) {
      this.user = this.$store.getters.getUserData;

      if (window.innerWidth > 768) {
        this.navbar = false;
      } else {
        this.navbar = true;
      }

      this.$store.dispatch('getCollectionsMusic', { 
        token: this.user.token
      }).then(response => {
        this.collections = this.$store.getters.getCollectionsMusic;
      }).catch(error => {
        if (!error.response) {
          this.notifyError("Network problem, please try again later..");
        }
      })
    }
  },
  methods: {
    refreshCollections() {
      this.$store.dispatch('getCollectionsMusic', { 
        token: this.user.token
      }).then(response => {
        this.collections = this.$store.getters.getCollectionsMusic;
        this.notify('Collections refreshed!');
      }).catch(error => {
        if (!error.response) {
          this.notifyError("Error refreshing collections");
        }
      })
    },
    createCollection() {
      this.notify('Create collection feature coming soon!');
    },
    openCollection(id) {
      this.notify(`Opening collection ${id}`);
    },
    playCollection(id) {
      this.notify(`Playing collection ${id}`);
    },
    editCollection(id) {
      this.notify(`Editing collection ${id}`);
    },
    shareCollection(id) {
      this.notify(`Sharing collection ${id}`);
    },
    deleteCollection(id) {
      this.notify(`Deleting collection ${id}`);
    },
    formatDate(date) {
      return new Date(date).toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    },
    checkScreenSize() {
      if (window.innerWidth > 768) {
        this.navbar = false;
      } else {
        this.navbar = true;
      }
    },
    handleClickMenu(link) {
      if (link === 'TryMuzE') {
        window.location.href = 'https://muze.muzible.com/';
      } else if (link === 'TryVerse') {
        window.location.href = 'https://verse.muzible.com/';
      } else {
        if (this.$route.name !== link) {
          this.$router.push({ name: link });
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@use './css/styles.scss';

/* Collections Header */
.collections-header {
  margin-bottom: 24px;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 12px;
}

.header-icon {
  font-size: 48px !important;
  color: white;
}

.header-title {
  font-family: "Darker Grotesque", sans-serif;
  font-size: 42px;
  font-weight: 700;
  color: white;
  margin: 0;
}

.header-subtitle {
  font-size: 16px;
  color: rgba(255, 255, 255, 0.7);
  margin: 0 0 20px 0;
  line-height: 1.5;
}

.header-actions {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.action-btn {
  font-family: "Darker Grotesque", sans-serif !important;
  font-size: 15px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  border-color: rgba(255, 255, 255, 0.3) !important;
}

.action-btn:hover {
  background: rgba(255, 255, 255, 0.1) !important;
  border-color: rgba(255, 255, 255, 0.5) !important;
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
}

.create-btn:hover {
  background: rgba(76, 175, 80, 0.2) !important;
  border-color: rgba(76, 175, 80, 0.6) !important;
}

/* Section */
.collections-section {
  margin-bottom: 32px;
}

.section-title {
  font-family: "Darker Grotesque", sans-serif;
  font-size: 28px;
  font-weight: 600;
  color: white;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 12px;
}

/* Filters Row */
.filters-row {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 16px;
  align-items: center;
}

@media (max-width: 768px) {
  .filters-row {
    grid-template-columns: 1fr;
  }
}

.search-field,
.sort-select {
  font-family: "Darker Grotesque", sans-serif !important;
}

:deep(.search-field .v-field),
:deep(.sort-select .v-field) {
  background: rgba(255, 255, 255, 0.05) !important;
  border: 1px solid rgba(255, 255, 255, 0.2) !important;
  border-radius: 8px !important;
  transition: all 0.3s ease;
}

:deep(.search-field .v-field:hover:not(.v-field--disabled)),
:deep(.sort-select .v-field:hover:not(.v-field--disabled)) {
  border-color: rgba(255, 255, 255, 0.4) !important;
}

:deep(.search-field .v-field--focused),
:deep(.sort-select .v-field--focused) {
  border-color: rgba(255, 255, 255, 0.6) !important;
}

:deep(.search-field .v-field__input),
:deep(.sort-select .v-field__input) {
  color: white !important;
  font-family: "Darker Grotesque", sans-serif !important;
  font-weight: 500;
}

.sort-select {
  min-width: 200px;
}

/* Collections Content */
.collections-content {
  min-height: 300px;
}

/* Collections Grid */
.collections-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 24px;
}

@media (max-width: 768px) {
  .collections-grid {
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
    gap: 16px;
  }
}

/* Collection Card */
.collection-card {
  background: linear-gradient(145deg, rgba(26, 26, 26, 0.95), rgba(18, 18, 18, 0.95));
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s ease;
}

.collection-card:hover {
  transform: translateY(-4px);
  border-color: rgba(255, 255, 255, 0.3);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
}

.collection-cover {
  position: relative;
  width: 100%;
  padding-top: 100%;
  background: rgba(0, 0, 0, 0.3);
  overflow: hidden;
}

.collection-cover img {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.collection-cover-placeholder {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, rgba(33, 33, 33, 0.8), rgba(22, 22, 22, 0.8));
}

.collection-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.collection-card:hover .collection-overlay {
  opacity: 1;
}

.play-btn {
  color: white !important;
  transition: transform 0.3s ease;
}

.play-btn:hover {
  transform: scale(1.1);
}

.collection-stats-overlay {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 5;
}

.tracks-chip {
  font-family: "Darker Grotesque", sans-serif !important;
  font-weight: 600 !important;
  backdrop-filter: blur(10px);
  background: rgba(0, 0, 0, 0.7) !important;
}

.collection-content {
  padding: 16px;
}

.collection-name {
  font-family: "Darker Grotesque", sans-serif;
  font-size: 18px;
  font-weight: 700;
  color: white;
  margin: 0 0 8px 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.collection-meta {
  margin: 0 0 12px 0;
}

.collection-description {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.6);
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.collection-description.empty {
  font-style: italic;
  color: rgba(255, 255, 255, 0.4);
}

.collection-info {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.info-item {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
}

.collection-actions {
  padding: 12px 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  display: flex;
  justify-content: flex-end;
  gap: 4px;
}

/* Empty State */
.collections-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  padding: 60px 20px;
  text-align: center;
  background: linear-gradient(145deg, rgba(26, 26, 26, 0.5), rgba(18, 18, 18, 0.5));
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
}

.empty-icon-wrapper {
  margin-bottom: 24px;
  animation: float 3s ease-in-out infinite;
}

@keyframes float {
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
}

.empty-title {
  font-family: "Darker Grotesque", sans-serif;
  font-size: 32px;
  font-weight: 700;
  color: white;
  margin-bottom: 12px;
}

.empty-subtitle {
  font-size: 16px;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 32px;
  max-width: 500px;
  line-height: 1.6;
}

.empty-actions {
  margin-bottom: 40px;
}

.empty-action-btn {
  font-family: "Darker Grotesque", sans-serif !important;
  font-size: 16px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  border-color: rgba(255, 255, 255, 0.3) !important;
  padding: 0 32px !important;
}

.empty-action-btn:hover {
  background: rgba(76, 175, 80, 0.2) !important;
  border-color: rgba(76, 175, 80, 0.6) !important;
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
}

.empty-features {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  max-width: 600px;
  width: 100%;
}

@media (max-width: 600px) {
  .empty-features {
    grid-template-columns: 1fr;
  }
}

.feature-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  transition: all 0.3s ease;
}

.feature-item:hover {
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 255, 255, 0.2);
  transform: translateY(-2px);
}

.feature-item span {
  font-family: "Darker Grotesque", sans-serif;
  font-size: 15px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.8);
}

/* Tips Section */
.tips-section {
  margin-top: 40px;
}

.tips-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 20px;
}

.tip-card {
  background: linear-gradient(145deg, rgba(26, 26, 26, 0.5), rgba(18, 18, 18, 0.5));
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 24px;
  text-align: center;
  transition: all 0.3s ease;
}

.tip-card:hover {
  border-color: rgba(255, 255, 255, 0.2);
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
}

.tip-icon {
  font-size: 40px !important;
  margin-bottom: 16px;
}

.tip-title {
  font-family: "Darker Grotesque", sans-serif;
  font-size: 20px;
  font-weight: 600;
  color: white;
  margin-bottom: 8px;
}

.tip-text {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.6;
  margin: 0;
}

/* Animations */
@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.collections-section,
.collections-empty-state,
.tips-section,
.collection-card {
  animation: slideIn 0.4s ease forwards;
}

/* Override Vuetify */
:deep(.v-btn:hover) > .v-btn__overlay {
  --v-hover-opacity: 0.1;
  opacity: 0.1;
}

:deep(.v-chip) {
  font-family: "Darker Grotesque", sans-serif !important;
  font-weight: 600 !important;
}

/* Responsive */
@media (max-width: 768px) {
  .header-title {
    font-size: 32px;
  }

  .header-actions {
    width: 100%;
  }

  .action-btn {
    flex: 1;
  }

  .section-title {
    font-size: 24px;
  }

  .tips-grid {
    grid-template-columns: 1fr;
  }
}
</style>